import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { css, StyleSheet } from 'aphrodite';
import { Dropdown } from 'react-bootstrap';
import { isEqual } from 'lodash';
import { LinkContainer } from 'react-router-bootstrap';
import { useLocation } from 'react-router-dom';

import { backgroundGrey1, backgroundGrey3, black, deeplavender } from 'lib/css/colors';
import useUserPermissions from 'lib/hooks/useUserPermissions';

function NestedDropdownItem({ item, index, level = 0 }) {
  const hasUserPermissions = useUserPermissions();
  const loc = useLocation();
  const isActive = isEqual(loc.pathname, item.url);
  const [show, setShow] = useState(false);

  const hasChildren = item.children && item.children.length > 0;

  if (!hasUserPermissions(item.permissions)) {
    return null;
  }

  if (hasChildren) {
    // For parent items with children, prevent the dropdown from closing
    const handleToggle = (isOpen, event, metadata) => {
      if (metadata.source === 'select') {
        // When item is clicked, toggle the show state
        setShow(!show);
        return false; // Prevent closing the parent dropdown
      }
      setShow(isOpen);
    };

    return (
      <Dropdown
        drop="right"
        key={index}
        className={css(styles.nestedDropdown)}
        show={show}
        onToggle={handleToggle}
      >
        <Dropdown.Toggle
          as="div" // Changed from Dropdown.Item to prevent the closing behavior
          className={css(styles.customToggle, level > 0 && styles.childItem)}
        >
          {item.title}
        </Dropdown.Toggle>
        <Dropdown.Menu className={css(styles.dropdownMenu, styles.nestedMenu)}>
          {item.children.map((childItem, childIndex) => (
            <NestedDropdownItem
              key={childIndex}
              item={childItem}
              index={childIndex}
              level={level + 1}
            />
          ))}
        </Dropdown.Menu>
      </Dropdown>
    );
  }

  // Regular menu item without children
  return (
    <LinkContainer
      className={css(styles.subLink, level > 0 && styles.childItem)}
      key={index}
      to={item.url}
    >
      <Dropdown.Item className={css(styles.dropdownItem, isActive && styles.activeItem)}>
        {item.title}
      </Dropdown.Item>
    </LinkContainer>
  );
}

const styles = StyleSheet.create({
  dropdownMenu: {
    padding: '0rem',
    minWidth: '6.4rem',
    boxShadow: `0px 4px 4px ${black}`,
  },
  dropdownItem: {
    padding: '0.5rem 0.7rem',
    textDecoration: 'none',
  },
  subLink: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'start',
    fontSize: '0.8rem',
  },
  nestedDropdown: {
    position: 'relative',
    width: '100%',
  },
  customToggle: {
    width: '100%',
    textAlign: 'left',
    position: 'relative',
    padding: '0.5rem 0.7rem',
    cursor: 'pointer',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    ':hover': {
      backgroundColor: backgroundGrey1,
    },
    fontSize: '0.8rem',
  },
  caretRight: {
    fontSize: '1.2rem',
    lineHeight: 1,
  },
  nestedMenu: {
    top: 0,
    marginLeft: '1px',
  },
  childItem: {
    paddingLeft: '1.5rem',
    fontSize: '0.75rem',
  },
  activeItem: {
    backgroundColor: backgroundGrey3,
    color: deeplavender,
  },
});

NestedDropdownItem.propTypes = {
  item: PropTypes.shape({
    url: PropTypes.string,
    title: PropTypes.string,
    permissions: PropTypes.array,
    children: PropTypes.array,
  }),
  index: PropTypes.number,
  level: PropTypes.number,
};

export default NestedDropdownItem;
